import { AiKpi, isToneKpi, metricValue } from '../GovernanceAnalyticsAiVisibility.utils';
import { IToneBearingRow, toneValue } from '../toneMetric';

/**
 * Reading one metric off a stats term row.
 *
 * The stats endpoints return each metric under THREE scopes on the same row: the bare name for the
 * customer's own entity, a `competitor*` twin, and an `average*` twin. Which one carries the value
 * depends on what kind of entity the row is about — a competitor's row has `competitorSentiment`
 * and a null `sentiment`, and vice versa.
 *
 * That shape is easy to bind wrongly, and a wrong binding does not throw: it yields `null`, which
 * silently became `0` and drew a flat line at the bottom of the chart. A flat zero is a claim —
 * "this brand is never recommended" — and it is indistinguishable from "this metric was not
 * returned". Keeping the binding here, with the fallback in one place and tests per metric, is what
 * stops the four metrics from drifting apart.
 */

/** A stats term row, in the shape the history and total endpoints actually return. */
export interface StatsTermMetrics extends IToneBearingRow {
  visibility?: number | null;
  shareOfVoice?: number | null;
  recommendation?: number | null;
  competitorVisibility?: number | null;
  competitorShareOfVoice?: number | null;
  competitorRecommendation?: number | null;
}

/** Clamps a rate to 0–1, and rejects anything that is not a finite number. */
function measuredRate(value?: number | null): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return Math.max(0, Math.min(1, value));
}

/**
 * The metric a term row carries, or undefined when the row does not carry it.
 *
 * Undefined is a real answer here and must survive: the caller decides how to draw a gap, and the
 * one thing it may never do is substitute zero.
 */
export function breakdownMetricOf(term: StatsTermMetrics, metric: AiKpi): number | undefined {
  // The tone metric is read off the row itself rather than off the flattened copy below: which
  // fields carry it is the switch's business, and the two scopes fall back the same way here as
  // every other metric does.
  if (isToneKpi(metric)) {
    return measuredRate(toneValue(term, 'own') ?? toneValue(term, 'competitor'));
  }

  return metricValue(
    {
      // Own scope first, competitor scope second — a row carries exactly one of the pair.
      visibility: measuredRate(term.visibility ?? term.competitorVisibility),
      shareOfVoice: measuredRate(term.shareOfVoice ?? term.competitorShareOfVoice),
      recommendation: measuredRate(term.recommendation ?? term.competitorRecommendation),
    },
    metric
  );
}

/** The same value as a whole percentage, or undefined when unmeasured. */
export function breakdownMetricPercentOf(term: StatsTermMetrics, metric: AiKpi): number | undefined {
  const rate = breakdownMetricOf(term, metric);
  return rate === undefined ? undefined : Math.round(rate * 100);
}

/**
 * Whether a series is entirely unmeasured — every point absent.
 *
 * The cross-check a reader performs by eye: a metric drawn as flat zero across the whole window
 * while the headline KPI for that same window is non-zero means the chart is not reading what the
 * KPI is reading. Expressed here so a test can assert it instead of a person noticing it.
 */
export function isSeriesEntirelyUnmeasured(points: readonly { value?: number }[]): boolean {
  return points.every((point) => point.value === undefined);
}
